import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { connectState, InputObservable } from '@y42/angular/util';
import { showLoaderFor } from '@y42/util/show-loader-for-x-ms';
import { GridOptions, GridReadyEvent } from 'ag-grid-community';
import { combineLatest, filter, map, Observable, tap } from 'rxjs';
import { OrchestrationNodeLogsDialogService } from '../orchestration-node-logs-dialog/orchestration-node-logs-dialog.service';
import {
  OrchestrationRunJobEntity,
  OrchestrationRunJobsListAgGridConfigService,
} from './orchestration-run-jobs-list-ag-grid-config.service';

@UntilDestroy()
@Component({
  selector: 'y42-orchestration-run-jobs-list',
  templateUrl: './orchestration-run-jobs-list.component.html',
  styleUrls: ['./orchestration-run-jobs-list.component.scss'],
  providers: [OrchestrationRunJobsListAgGridConfigService, OrchestrationNodeLogsDialogService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrchestrationRunJobsListComponent {
  constructor(
    private readonly orchestrationRunJobsListAgGridConfigService: OrchestrationRunJobsListAgGridConfigService,
  ) {
    this.emitRedirectToAssetOnChange().pipe(untilDestroyed(this)).subscribe();
  }

  @Input()
  @InputObservable()
  jobs?: OrchestrationRunJobEntity[];
  jobs$!: Observable<OrchestrationRunJobEntity[] | undefined>;

  @Input()
  @InputObservable()
  loader = false;
  private readonly loader$!: Observable<boolean>;

  @Input()
  @InputObservable()
  allowAssetRedirection = false;
  private readonly allowAssetRedirection$!: Observable<boolean>;

  @Output() redirectToAsset = new EventEmitter<string>();

  public readonly state = connectState(this, {
    hasJobs: this.jobs$.pipe(map((assetsWithDeps) => !!assetsWithDeps?.length)),
    jobs: this.jobs$,
    columnDefs: combineLatest([this.jobs$, this.allowAssetRedirection$]).pipe(
      map(([jobs, allowAssetRedirection]) => {
        const showLogsButtonColumn = jobs?.some((job) => !!job.jobId);
        return this.orchestrationRunJobsListAgGridConfigService.getColumnDefs({
          showLogsButtonColumn,
          allowAssetRedirection,
        });
      }),
      tap(() => this.gridOptions?.columnApi?.autoSizeAllColumns()),
    ),
    loader: this.loader$.pipe(showLoaderFor(500)),
  });

  readonly gridOptions: GridOptions = this.orchestrationRunJobsListAgGridConfigService.gridOptions;

  onGridReady(params: GridReadyEvent) {
    this.orchestrationRunJobsListAgGridConfigService.gridReady(params);
  }

  private emitRedirectToAssetOnChange() {
    return this.orchestrationRunJobsListAgGridConfigService.redirectToAsset$.pipe(
      filter(Boolean),
      tap((assetPath) => this.redirectToAsset.emit(assetPath)),
    );
  }
}
